import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../tailwind.css';
import env from '../../config';

// URLs de Configuração (Assumindo que os arquivos de som estão na pasta 'public')
const backendUrl = env.backendUrl;
const API_LIST_URL = backendUrl + '/api/agenda/list';
const API_UPDATE_URL = backendUrl + '/api/agenda/update-status';

// Caminhos dos Sons (Assumindo que estão acessíveis via URL raiz, ex: public/assets/...)
const soundSuccess = '/assets/sound-success.wav';
const soundFail = '/assets/sound-fail.wav';

// Componente auxiliar para exibir o badge de status (inalterado)
const StatusBadge = ({ status }) => {
    let colorClasses = '';
    let text = '';

    switch (status) {
        case 'pending':
            colorClasses = 'bg-red-100 text-red-800';
            text = 'Pendente';
            break;
        case 'in_progress':
            colorClasses = 'bg-yellow-100 text-yellow-800';
            text = 'Em andamento';
            break;
        case 'completed':
            colorClasses = 'bg-green-100 text-green-800';
            text = 'Concluído';
            break;
        default:
            colorClasses = 'bg-gray-100 text-gray-800';
            text = 'Desconhecido';
    }

    return (
        <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium capitalize ${colorClasses}`}>
            {text}
        </span>
    );
};


function Agenda() {
    const [allAgendaTasks, setAllAgendaTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeResponsavel, setActiveResponsavel] = useState(null);
    const navigate = useNavigate();

    // 💥 CORREÇÃO DO HOOK: Criação das referências de áudio DENTRO do componente
    const successAudioRef = useRef(null);
    const failAudioRef = useRef(null);


    // ===============================================
    // Lógica de Áudio (Pré-carregamento)
    // ===============================================
    useEffect(() => {
        try {
            // Pré-carrega o som de sucesso
            const successAudio = new Audio(soundSuccess);
            successAudio.volume = 0.5;
            successAudioRef.current = successAudio;

            // Pré-carrega o som de falha (ou reset/reversão)
            const failAudio = new Audio(soundFail);
            failAudio.volume = 0.5;
            failAudioRef.current = failAudio;

        } catch (e) {
            // Em navegadores restritivos (como Safari mobile), pode falhar, mas o código continua
            console.warn("Não foi possível pré-carregar o áudio. O som pode ter um delay:", e);
        }
    }, []);

    // Funções de reprodução que usam as referências pré-carregadas
    const playSuccessSound = () => {
        const audio = successAudioRef.current;
        if (audio) {
            audio.currentTime = 0; // Reseta para tocar instantaneamente
            audio.play().catch(error => console.warn("Erro ao reproduzir som de sucesso:", error));
        }
    };

    const playFailSound = () => {
        const audio = failAudioRef.current;
        if (audio) {
            audio.currentTime = 0; // Reseta para tocar instantaneamente
            audio.play().catch(error => console.warn("Erro ao reproduzir som de falha:", error));
        }
    };


    // ===============================================
    // Lógica de Fetch e Persistência do Responsável
    // ===============================================
    useEffect(() => {
        const fetchTasks = async () => {
            try {
                // Fetch dos dados
                const response = await fetch(API_LIST_URL);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();

                const tasks = data.data;
                setAllAgendaTasks(tasks);

                // Lógica de Persistência na URL (Leitura)
                // Se houver um responsável logado no sessionStorage, prioriza ele
                const responsavelFromSession = sessionStorage.getItem('responsavel');

                if (tasks.length > 0) {
                    // Apenas usa o responsável presente em sessionStorage; se não houver, não mostra nada
                    if (responsavelFromSession && tasks.some(t => t.responsavel === responsavelFromSession)) {
                        setActiveResponsavel(responsavelFromSession);
                    } else {
                        // Não faz fallback: requer login para ver tarefas
                        setActiveResponsavel(null);
                    }
                }

            } catch (err) {
                console.error("Erro ao buscar a agenda:", err);
                setError("Não foi possível carregar a agenda. Verifique a conexão com a API.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchTasks();
    }, []);

    // Agrupamento dos dados por Responsável (inalterado)
    const groupedTasks = useMemo(() => {
        if (!allAgendaTasks.length) return {};

        return allAgendaTasks.reduce((acc, task) => {
            const responsavel = task.responsavel;
            if (!acc[responsavel]) {
                acc[responsavel] = [];
            }
            acc[responsavel].push(task);
            return acc;
        }, {});
    }, [allAgendaTasks]);

    const responsaveis = Object.keys(groupedTasks);
    const currentTasks = activeResponsavel ? allAgendaTasks.filter(t => t.responsavel === activeResponsavel) : [];

    // opção para mostrar/ocultar tarefas concluídas
    const [showCompleted, setShowCompleted] = useState(true);

    const completedCount = useMemo(() => {
        return currentTasks.filter(t => t.status === 'completed').length;
    }, [currentTasks]);

    const displayedTasks = useMemo(() => {
        if (showCompleted) return currentTasks;
        return currentTasks.filter(t => t.status !== 'completed');
    }, [currentTasks, showCompleted]);

    // Resumo de pontos (total e concluídos) para o responsável ativo
    const pointsSummary = useMemo(() => {
        if (!currentTasks || currentTasks.length === 0) return { total: 0, completed: 0, percent: 0 };
        const total = currentTasks.reduce((acc, t) => acc + (Number(t.points) || 0), 0);
        const completed = currentTasks.filter(t => t.status === 'completed').reduce((acc, t) => acc + (Number(t.points) || 0), 0);
        const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
        return { total, completed, percent };
    }, [currentTasks]);

    // animated percent (suaviza a mudança do número)
    const [animatedPercent, setAnimatedPercent] = useState(pointsSummary.percent || 0);
    const prevPercentRef = useRef(pointsSummary.percent || 0);

    useEffect(() => {
        const start = prevPercentRef.current || 0;
        const end = pointsSummary.percent || 0;
        if (start === end) {
            setAnimatedPercent(end);
            prevPercentRef.current = end;
            return;
        }

        const duration = 600; // ms
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
        const startTime = performance.now();
        let rafId = null;

        const tick = (now) => {
            const elapsed = now - startTime;
            const t = Math.min(1, elapsed / duration);
            const eased = easeOutCubic(t);
            const value = Math.round(start + (end - start) * eased);
            setAnimatedPercent(value);
            if (t < 1) {
                rafId = requestAnimationFrame(tick);
            } else {
                prevPercentRef.current = end;
            }
        };

        rafId = requestAnimationFrame(tick);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [pointsSummary.percent]);

    // (removida) troca manual de responsável — agora a agenda mostra somente o responsável logado


    // ===============================================
    // Lógica de Atualização de Status
    // ===============================================
    // Logout simples: limpa sessionStorage e redireciona para a tela de login
    const logout = () => {
        try {
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('responsavel');
            sessionStorage.removeItem('isAuthenticated');
        } catch (e) {
            // ignore
        }
        setActiveResponsavel(null);
        navigate('/login-images');
    };
    const handleUpdateStatus = async (task) => {
        let newStatus;
        if (task.status === 'pending') {
            newStatus = 'in_progress';
        } else if (task.status === 'in_progress') {
            newStatus = 'completed';
        } else if (task.status === 'completed') {
            // Permite reverter de 'completed' para 'pending'
            newStatus = 'pending';
        } else {
            return;
        }

        setIsLoading(true);

        try {
            // Chamada à API para atualizar no Backend
            const response = await fetch(API_UPDATE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    agenda_id: task.id,
                    status: newStatus
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Falha na API com status: ${response.status}`);
            }

            // Atualizar o estado local (UI)
            setAllAgendaTasks(prevTasks =>
                prevTasks.map(t =>
                    t.id === task.id ? { ...t, status: newStatus } : t
                )
            );

            // Tocar o som
            if (newStatus === 'completed') {
                playSuccessSound();
            } else if (newStatus === 'pending') {
                playFailSound();
            }

        } catch (err) {
            console.error("Erro ao atualizar o status:", err);
            alert(`Erro ao atualizar a tarefa: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // ... (Código de Renderização) ...

    if (isLoading && allAgendaTasks.length === 0) {
        return <div className="p-8 text-center text-blue-500 text-lg">Carregando a agenda...</div>;
    }

    if (error) {
        return <div className="p-8 text-center text-red-500 font-bold text-lg">{error}</div>;
    }

    if (responsaveis.length === 0) {
        return <div className="p-8 text-center text-gray-500 text-lg">Nenhuma tarefa encontrada na agenda.</div>;
    }

    // Se não há responsável ativo (ex.: usuário não fez login), pedir para logar
    if (!activeResponsavel) {
        return (
            <div className="p-8 text-center">
                <p className="text-lg text-gray-700">Nenhum responsável logado.</p>
                <p className="text-sm text-gray-500">Por favor, faça login para ver suas tarefas.</p>
                <div style={{marginTop:12}}>
                    <a href="/login-images" className="default-btn">Ir para login</a>
                </div>
            </div>
        );
    }

    // 4. Componente Principal com Tabela Responsiva
    return (
        <div className="p-4 sm:p-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Agenda de Tarefas</h1>
                <div>
                    <button onClick={logout} className="default-btn" style={{background:'#ef4444', color:'#fff'}}>Sair</button>
                </div>
            </div>

            {/* Mostra apenas as tarefas do responsável logado */}

            {/* CONTEÚDO DA AGENDA (FILTRADO) */}
            <div className="mt-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold mb-4 text-gray-700">Tarefas de {activeResponsavel}</h2>
                    <div className="flex items-center gap-4">
                        <div className="hidden sm:block text-sm text-gray-600">Concluídas: <strong>{pointsSummary.completed}</strong> / Total: <strong>{pointsSummary.total}</strong> pontos <span className="text-gray-500">({animatedPercent}%)</span></div>
                        <label className="flex items-center text-sm text-gray-600">
                            <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} className="mr-2" /> Mostrar concluídas ({completedCount})
                        </label>
                    </div>
                </div>

                    {/* versão mobile: destaque em bloco grande */}
                    <div className="sm:hidden mb-4 p-3 bg-indigo-50 rounded-lg text-center">
                        <div className="text-base font-semibold text-indigo-700">Pontos</div>
                        <div className="text-lg font-bold text-indigo-900">{pointsSummary.completed} <span className="text-sm font-normal text-indigo-700">/ {pointsSummary.total}</span> <span className="text-base font-semibold text-indigo-700">({animatedPercent}%)</span></div>
                            <div className="text-sm text-indigo-600">concluídos / total</div>
                    </div>

                {/* LISTA DE CARDS PARA CELULAR */}
                <div className="sm:hidden space-y-4">
                    {displayedTasks.map((task) => (
                        <div key={task.id} className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition duration-150">

                            <div className="flex justify-between items-start mb-2">
                                <span className="text-base font-bold text-gray-900">{task.label}</span>
                                <StatusBadge status={task.status} />
                            </div>

                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <div className="text-sm text-gray-500 font-medium">
                                    Frequência: <span className="font-semibold text-gray-700">{task.frequency}d</span>
                                </div>
                                <div className="text-sm text-gray-500 font-medium">
                                    Pontos: <span className="font-semibold text-gray-700">{task.points}</span>
                                </div>

                                <button
                                    onClick={() => handleUpdateStatus(task)}
                                    disabled={isLoading}
                                    className={`px-3 py-1 text-sm font-semibold rounded-full transition duration-150 
                                        ${task.status === 'completed'
                                            ? 'bg-green-600 text-white hover:bg-green-700'
                                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                        }`}
                                >
                                    {/* TEXTO DO BOTÃO DINÂMICO */}
                                    {task.status === 'pending' ? 'INICIAR' :
                                        task.status === 'in_progress' ? 'CONCLUIR' : 'REVERTER'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>


                {/* TABELA TRADICIONAL PARA DESKTOP */}
                <div className="hidden sm:block overflow-x-auto shadow-lg rounded-lg border border-gray-200">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr className="bg-gray-100 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                <th className="px-6 py-3">Atividade</th><th className="px-6 py-3 text-center">Freq.</th><th className="px-6 py-3 text-center">Status</th><th className="px-6 py-3 text-center">Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedTasks.map((task) => (
                                <tr key={task.id} className="border-b border-gray-100 hover:bg-indigo-50/50 transition duration-150">
                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{task.label}</td>
                                    <td className="px-6 py-4 text-sm text-gray-500 text-center">{task.frequency}d</td>
                                    <td className="px-6 py-4 text-center">
                                        <StatusBadge status={task.status} />
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button
                                            onClick={() => handleUpdateStatus(task)}
                                            disabled={isLoading}
                                            className={`px-4 py-2 text-sm font-semibold rounded-full transition duration-150 
                                                ${task.status === 'completed'
                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                }`}
                                        >
                                            {/* TEXTO DO BOTÃO DINÂMICO */}
                                            {task.status === 'pending' ? 'INICIAR' :
                                                task.status === 'in_progress' ? 'CONCLUIR' : 'REVERTER'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default Agenda;